<template>
    <div>
        <template v-if="isConfirmPopupVisible">
            <segmentationFixConfirmPopup @onClose="cancelResult" @onSave="confirmResult" :show="true" 
                :result="confirmPopup.result" :selection="confirmPopup.selection"
                :leftPosition="confirmPopup.position.x" :topPosition="confirmPopup.position.y">
            </segmentationFixConfirmPopup>
        </template>

        <div class="segmentation-fix-trigger animate-fade-in" v-if="isTriggerVisible" @click="triggerFix"
            @mouseover="onMouseOverTrigger" @mouseleave="onMouseLeaveTrigger" :style="{
                top: trigger.position.y + 'px',
                left: trigger.position.x + 'px',
            }">
            <FixSegmentacaoDenteSvgIcon class="icon" />
            <span class="text">Corrigir</span>
        </div>
    </div>
</template>

<script>
import { evtBus } from '@/main';
import { utils } from '@/globals/utils';

import segmentationFixConfirmPopup from './popup/segmentationFixConfirmPopup.vue';
import FixSegmentacaoDenteSvgIcon from '@/assets/icons/toolbar/fix-segmentacao-dente.svg';

export default {
    name: 'segmentationFixHandler',
    components: {
        segmentationFixConfirmPopup,
        FixSegmentacaoDenteSvgIcon,
    },
    props: {
    },

    data() {
        return {
            trigger: {
                position: {
                    x: 0,
                    y: 0,
                },
                visible: false,
                hideTimeout: null,
            },
            confirmPopup: {
                result: null,
                position: {
                    x: 0,
                    y: 0,
                },
                selection: {
                    rect: null,
                    mousePos: {
                        x: 0,
                        y: 0,
                    },
                },
            },
        }
    },

    computed: {

        isConfirmPopupVisible() {
            return this.confirmPopup.result != null;
        },

        isTriggerVisible() {
            if (this.isConfirmPopupVisible)
                return false;
            return this.trigger.visible;
        },
    },

    methods: {

        /**
         * Decides where to place the popup relative to an annotation.
         *
         * Normally the popup is positioned just outside `locationsRect`, on the
         * side closest to `toolRect`. If `locationsRect` is invalid (empty, i.e.
         * { x: 0, y: 0, width: 0, height: 0 }), it falls back to positioning the
         * popup around `toolRect` itself, using the same screen-space logic.
         */
        calculatePopupPosition(locationsRect, toolRect) {
            const isRectInvalid = (rect) =>
                !rect || (rect.x === 0 && rect.y === 0 && rect.width === 0 && rect.height === 0);

            const targetRect = isRectInvalid(locationsRect) ? toolRect : locationsRect;
            return this.placePopupAroundRect(targetRect, toolRect);
        },

        /**
         * Positions a 350x350 popup just outside `targetRect`, choosing the side
         * closest to `referenceRect`. Sides are tried in order of proximity; the
         * first one where the popup fully fits on screen wins. If none fit, the
         * closest side is used as a fallback.
         *
         * The popup is always kept outside `targetRect` on the placement axis;
         * only the cross axis is clamped so it never re-enters the rect.
         */
        placePopupAroundRect(targetRect, referenceRect) {
            const POPUP_WIDTH = 330;
            const POPUP_HEIGHT = 150;
            const GAP = 10;
            const MARGIN = {
                top: 16,
                bottom: 100,
                left: 16,
                right: 16,
            };

            const rect = {
                left: targetRect.x,
                top: targetRect.y,
                right: targetRect.x + targetRect.width,
                bottom: targetRect.y + targetRect.height,
            };

            const referenceCenter = {
                x: referenceRect.x + referenceRect.width / 2,
                y: referenceRect.y + referenceRect.height / 2,
            };

            const clampX = (x, side) => Math.max(MARGIN[side], Math.min(x, window.innerWidth - POPUP_WIDTH - MARGIN[side]));
            const clampY = (y, side) => Math.max(MARGIN[side], Math.min(y, window.innerHeight - POPUP_HEIGHT - MARGIN[side]));

            const sides = [
                {
                    name: 'right',
                    distance: Math.abs(referenceCenter.x - rect.right),
                    build: () => ({ x: rect.right + GAP, y: clampY(rect.top, 'right') }),
                    fits: (pos) => pos.x + POPUP_WIDTH + MARGIN.right <= window.innerWidth,
                },
                {
                    name: 'left',
                    distance: Math.abs(referenceCenter.x - rect.left),
                    build: () => ({ x: rect.left - GAP - POPUP_WIDTH, y: clampY(rect.top, 'left') }),
                    fits: (pos) => pos.x >= MARGIN.left,
                },
                {
                    name: 'bottom',
                    distance: Math.abs(referenceCenter.y - rect.bottom),
                    build: () => ({ x: clampX(rect.left, 'bottom'), y: rect.bottom + GAP }),
                    fits: (pos) => pos.y + POPUP_HEIGHT + MARGIN.bottom <= window.innerHeight,
                },
                {
                    name: 'top',
                    distance: Math.abs(referenceCenter.y - rect.top),
                    build: () => ({ x: clampX(rect.left, 'top'), y: rect.top - GAP - POPUP_HEIGHT }),
                    fits: (pos) => pos.y >= MARGIN.top,
                },
            ];

            // Closest side to the reference rect first.
            sides.sort((a, b) => a.distance - b.distance);

            for (const side of sides) {
                const pos = side.build();
                if (side.fits(pos)) {
                    return pos;
                }
            }

            // Nothing fits on screen: fall back to the closest side.
            return sides[0].build();
        },

        triggerFix() {
            evtBus.$emit('evt_segmentacaoFix_triggerFix');
            this.hideTrigger();
        },

        showConfirmPopup(payload) {
            if (!payload.result)
                return;
            this.confirmPopup.result = payload.result;
            this.confirmPopup.selection = payload.selection;

            const segmentationBoundingRect = payload.selection.rect;
            this.confirmPopup.position = this.calculatePopupPosition(segmentationBoundingRect, segmentationBoundingRect);
        },

        closeConfirmPopup() {
            this.confirmPopup = {
                result: null,
                position: {
                    x: 0,
                    y: 0,
                },
                selection: {
                    rect: null,
                    mousePos: {
                        x: 0,
                        y: 0,
                    },
                },
            };
        },

        confirmResult(result) {
            evtBus.$emit('evt_segmentacaoFix_confirmResult', { result });
            this.closeConfirmPopup();
        },

        cancelResult() {
            evtBus.$emit('evt_segmentacaoFix_cancelResult');
            this.closeConfirmPopup();
        },

        showTrigger(payload) {
            // console.log("showOpener: ", payload);
            this.trigger.position = payload.position;
            this.trigger.visible = true;
        },

        delayedHideTrigger() {
            clearTimeout(this.trigger.hideTimeout);
            this.trigger.hideTimeout = setTimeout(() => {
                this.hideTrigger();
            }, 1000);
        },

        hideTrigger() {
            this.trigger.position = {
                x: 0,
                y: 0,
            };
            this.trigger.visible = false;
        },

        onMouseOverTrigger() {
            clearTimeout(this.trigger.hideTimeout);
        },

        onMouseLeaveTrigger() {
            this.delayedHideTrigger();
        },
    },

    mounted() {
        evtBus.$on('evt_segmentacaoFix_showTrigger', (payload) => {
            this.showTrigger(payload);
        });

        evtBus.$on('evt_segmentacaoFix_hideTrigger', () => {
            this.delayedHideTrigger();
        });

        evtBus.$on('evt_segmentacaoFix_showConfirmPopup', (payload) => {
            this.showConfirmPopup(payload);
        });

        evtBus.$on('evt_segmentacaoFix_closeConfirmPopup', () => {
            this.closeConfirmPopup();
        });
    },
}
</script>

<style scoped>
.segmentation-fix-trigger {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    position: absolute;
    background-color: var(--color-primary);
    color: var(--color-white);
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--radius-md);
    transition: opacity 0.2s ease-in-out;
    z-index: 1000;
    cursor: pointer;
}

.segmentation-fix-trigger:hover {
    opacity: 0.8;
}

.segmentation-fix-trigger .icon {
    width: 15px;
    height: 15px;
}

.segmentation-fix-trigger .text {
    font-size: var(--text-xs);
    font-weight: 600;
    text-align: center;
}
</style>